import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Star, ArrowRight, ShieldCheck, Truck, Leaf, Clock } from 'lucide-react';
import { PolicyModal } from '../components/PolicyModal';
import { useAnalytics } from '../hooks/useAnalytics';
type PolicyType = 'privacy' | 'terms' | 'refund';
const bodyAreas = [{
  id: 'neck-shoulders',
  title: 'Neck & Shoulders',
  description: 'Tension from desk work, stress, or poor posture',
  path: '/ADVERT-NECK&SHOULDERS',
  icon: ArrowRight
}, {
  id: 'upper-lower-back',
  title: 'Upper & Lower Back',
  description: 'Stiffness from sitting, lifting, or strain',
  path: '/ADVERT-UPPER&LOWERBACK',
  icon: ArrowRight
}, {
  id: 'hips-glutes',
  title: 'Hips & Glutes',
  description: 'Tightness from prolonged sitting or activity',
  path: '/ADVERT-HIPS&GLUTES',
  icon: ArrowRight
}, {
  id: 'knee-joints',
  title: 'Knee Joints',
  description: 'Discomfort from movement, age, or overuse',
  path: '/ADVERT-KNEES-JOINTS',
  icon: ArrowRight
}, {
  id: 'feet-ankles',
  title: 'Feet & Ankles',
  description: 'Fatigue from standing, walking, or swelling',
  path: '/ADVERT-FEET&ANKLE',
  icon: ArrowRight
}, {
  id: 'hands-wrists',
  title: 'Hands & Wrists',
  description: 'Strain from repetitive tasks or typing',
  path: '/ADVERT-HANDS&WRIST',
  icon: ArrowRight
}];
export function OptionsPage() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const {
    trackPageView,
    trackClick
  } = useAnalytics();
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  return <div className="min-h-screen bg-[#F8F7F3] text-[#2C3E50]" data-id="element-3173">
      {/* Header - Reduced padding on mobile */}
      <header className="sticky top-0 z-50 bg-[#FEFEFF] border-b border-[#E87722]/10" data-id="element-3174">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-4 flex justify-center sm:justify-start" data-id="element-3175">
          <Link to="/home" className="opacity-90 hover:opacity-100 transition-opacity" data-id="element-3176">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-5 sm:h-7 w-auto" loading="eager" fetchpriority="high" width="150" height="28" data-id="element-3177" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-16" data-id="element-3178">
        {/* Hero Section - Reduced spacing on mobile */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-6 sm:mb-16" data-id="element-3179">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-light text-[#2C3E50] mb-3 sm:mb-6 tracking-tight" data-id="element-3180">
            Where Does Your Body Hold Tension or Pressure?
          </h1>
          <p className="text-base sm:text-xl text-[#2C3E50]/80 font-light leading-relaxed max-w-2xl mx-auto mb-3 sm:mb-4" data-id="element-3181">
            Discomfort doesn't show up the same way for everyone. Select the
            area where your body feels tight, heavy, or restricted — especially
            by the end of the day.
          </p>
        </motion.div>

        {/* Featured: Swollen Legs CTA - NEW */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mb-6 sm:mb-8" data-id="element-3182">
          <Link to="/ADVERT-SWOLLEN-LEGS" className="block h-full" onClick={() => trackClick('Featured: Swollen Legs')} data-id="element-3183">
            <motion.div whileHover={{
            y: -4,
            boxShadow: '0 10px 30px -10px rgba(232, 119, 34, 0.2)'
          }} className="relative h-full bg-white rounded-xl p-4 sm:p-6 border border-[#E87722]/10 hover:border-[#E87722]/30 transition-all duration-300 shadow-sm hover:shadow-md group" data-id="element-3184">
              {/* Most Popular Badge */}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-[#E87722]/10 px-2 sm:px-3 py-1 rounded-full" data-id="element-3185">
                <span className="text-[#E87722] text-[10px] sm:text-xs font-medium tracking-wider uppercase" data-id="element-3186">
                  Most Popular
                </span>
              </div>

              <div className="flex flex-col h-full" data-id="element-3187">
                <div className="mb-3 sm:mb-4" data-id="element-3188">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#E87722]/10 flex items-center justify-center group-hover:bg-[#E87722]/20 transition-colors" data-id="element-3189">
                    <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-[#E87722]" data-id="element-3190" />
                  </div>
                </div>

                <h3 className="text-base sm:text-lg font-medium text-[#2C3E50] mb-1 sm:mb-2 group-hover:text-[#E87722] transition-colors" data-id="element-3191">
                  Swollen Legs & Ankles
                </h3>

                <p className="text-xs sm:text-sm text-[#2C3E50]/70 font-light leading-relaxed flex-grow" data-id="element-3192">
                  Heavy, tight legs by evening? Discover how natural warmth
                  helps circulation flow instead of pooling
                </p>

                <div className="mt-3 sm:mt-4 flex items-center text-[#E87722] text-xs sm:text-sm font-medium" data-id="element-3193">
                  <span data-id="element-3194">Select This Area</span>
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-3195" />
                </div>
              </div>
            </motion.div>
          </Link>
        </motion.div>

        {/* Selector Grid - Reduced gaps and padding on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-16" data-id="element-3196">
          {bodyAreas.map((area, index) => <Link key={area.id} to={area.path} className="block h-full" onClick={() => trackClick(`Selected Area: ${area.title}`)} data-id="element-3197">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} whileHover={{
            y: -4,
            boxShadow: '0 10px 30px -10px rgba(232, 119, 34, 0.2)'
          }} className="h-full bg-white rounded-xl p-4 sm:p-6 border border-[#E87722]/10 hover:border-[#E87722]/30 transition-all duration-300 shadow-sm hover:shadow-md group" data-id="element-3198">
                <div className="flex flex-col h-full" data-id="element-3199">
                  <div className="mb-3 sm:mb-4" data-id="element-3200">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#E87722]/10 flex items-center justify-center group-hover:bg-[#E87722]/20 transition-colors" data-id="element-3201">
                      <area.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#E87722]" data-id="element-3202" />
                    </div>
                  </div>

                  <h3 className="text-base sm:text-lg font-medium text-[#2C3E50] mb-1 sm:mb-2 group-hover:text-[#E87722] transition-colors" data-id="element-3203">
                    {area.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-[#2C3E50]/70 font-light leading-relaxed flex-grow" data-id="element-3204">
                    {area.description}
                  </p>

                  <div className="mt-3 sm:mt-4 flex items-center text-[#E87722] text-xs sm:text-sm font-medium" data-id="element-3205">
                    <span data-id="element-3206">Select This Area</span>
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-3207" />
                  </div>
                </div>
              </motion.div>
            </Link>)}
        </div>

        {/* Reassurance Section - Reduced spacing on mobile */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-2xl mx-auto text-center mb-8 sm:mb-24" data-id="element-3208">
          <div className="inline-block w-16 h-1 bg-[#E87722]/20 rounded-full mb-4 sm:mb-8" data-id="element-3209"></div>
          <p className="text-base sm:text-2xl font-light text-[#2C3E50] leading-relaxed italic" data-id="element-3210">
            "GINEXT doesn't mask discomfort. It supports circulation with
            natural warmth so your body can release pressure on its own."
          </p>
        </motion.div>

        {/* Footer Note - Reduced padding on mobile */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl p-4 sm:p-8 border border-[#E87722]/10 shadow-sm text-center" data-id="element-3211">
          <h4 className="text-base sm:text-lg font-medium text-[#2C3E50] mb-1 sm:mb-2" data-id="element-3212">
            Not sure where to start?
          </h4>
          <p className="text-sm sm:text-base text-[#2C3E50]/70 font-light mb-4 sm:mb-6" data-id="element-3213">
            Many people choose the area that feels heaviest by evening.
          </p>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-8 text-xs sm:text-sm text-[#2C3E50]/60 font-medium uppercase tracking-wide" data-id="element-3214">
            <div className="flex items-center gap-2" data-id="element-3215">
              <Leaf className="w-3 h-3 sm:w-4 sm:h-4 text-[#E87722]" data-id="element-3216" />
              <span data-id="element-3217">100% Natural</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-3218">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-[#E87722]" data-id="element-3219" />
              <span data-id="element-3220">Daily Use Safe</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-3221">
              <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4 text-[#E87722]" data-id="element-3222" />
              <span data-id="element-3223">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer - Updated to match home page */}
      <footer className="bg-charcoal text-gray-400 py-4 mt-8 text-center text-xs" data-id="element-3224">
        <div className="max-w-4xl mx-auto px-4" data-id="element-3225">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-3226">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-3227">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-3228">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-3229">
              Refund Policy
            </button>
          </div>
          <p data-id="element-3230">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-3231">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-3232" />
    </div>;
}